import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider.jsx";
import { hasRole } from "../../auth/roles.js";
import {
    createRekoAdminCase,
    deleteRekoAdminCase,
    fetchRekoAdminCases,
    fetchRekoCases,
    fetchRekoDrivers,
    fetchRekoRaces,
    fetchRekoRules,
    openBulkRekoAdminCases,
    saveRekoOpinion,
    updateRekoAdminCase,
    fetchRekoLeadOpinions,
    saveRekoLeadFinalDecision,
    publishRekoLeadFinalDecision,
    reopenRekoLeadCase,
} from "../../lib/rekoApi.js";

const EMPTY_FORM = {
    incident_timestamp: "",
    initial_rule_id: "",
    driver_1_season_driver_id: "",
    driver_2_season_driver_id: "",
    description: "",
};

function prettyError(err) {
    if (!err) return "Unbekannter Fehler";
    if (err instanceof Error) return err.message || "Fehler";
    return String(err);
}

function statusLabel(status) {
    const s = String(status || "").toLowerCase();

    if (s === "draft") return "Draft";
    if (s === "open") return "An ReKo freigegeben";
    if (s === "finalized") return "Finalisiert";
    if (s === "published") return "Veröffentlicht";
    if (s === "canceled") return "Abgebrochen";

    return status || "Unbekannt";
}

function StatusBadge({ status }) {
    const s = String(status || "").toLowerCase();

    const style = {
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "3px 9px",
        fontSize: 12,
        fontWeight: 700,
        border: "1px solid rgba(255,255,255,.12)",
    };

    if (s === "draft") {
        style.color = "#ffd470";
        style.background = "rgba(255,212,112,.10)";
    } else if (s === "open") {
        style.color = "#8ecbff";
        style.background = "rgba(142,203,255,.10)";
    } else if (s === "published") {
        style.color = "#8bd9a6";
        style.background = "rgba(139,217,166,.10)";
    } else {
        style.color = "var(--text)";
        style.background = "rgba(255,255,255,.06)";
    }

    return <span style={style}>{statusLabel(status)}</span>;
}

function OpinionEditor({ item, rules, onSaved }) {
    const existing = item?.my_opinion || null;
    const existingPenalty = existing?.penalties?.[0] || null;

    const [decisionType, setDecisionType] = useState(existing?.decision_type || "");
    const [conflictType, setConflictType] = useState(existing?.conflict_type || "");
    const [reasoning, setReasoning] = useState(existing?.reasoning || "");

    const [penalizedDriverId, setPenalizedDriverId] = useState(
        existingPenalty?.penalized_season_driver_id
            ? String(existingPenalty.penalized_season_driver_id)
            : ""
    );

    const [penaltyRuleId, setPenaltyRuleId] = useState(
        existingPenalty?.rule_id
            ? String(existingPenalty.rule_id)
            : ""
    );

    const [penaltySeconds, setPenaltySeconds] = useState(
        existingPenalty?.penalty_seconds
            ? String(existingPenalty.penalty_seconds)
            : ""
    );

    const [penaltyPoints, setPenaltyPoints] = useState(
        existingPenalty?.penalty_points
            ? String(existingPenalty.penalty_points)
            : ""
    );

    const [penaltyText, setPenaltyText] = useState(existingPenalty?.penalty_text || "");
    const [busy, setBusy] = useState(false);
    const [localError, setLocalError] = useState("");

    const isLocked = item?.status !== "open";
    const drivers = Array.isArray(item?.drivers) ? item.drivers : [];

    useEffect(() => {
        const opinion = item?.my_opinion || null;
        const penalty = opinion?.penalties?.[0] || null;

        setDecisionType(opinion?.decision_type || "");
        setConflictType(opinion?.conflict_type || "");
        setReasoning(opinion?.reasoning || "");
        setPenalizedDriverId(
            penalty?.penalized_season_driver_id
                ? String(penalty.penalized_season_driver_id)
                : ""
        );
        setPenaltyRuleId(penalty?.rule_id ? String(penalty.rule_id) : "");
        setPenaltySeconds(penalty?.penalty_seconds ? String(penalty.penalty_seconds) : "");
        setPenaltyPoints(penalty?.penalty_points ? String(penalty.penalty_points) : "");
        setPenaltyText(penalty?.penalty_text || "");
        setLocalError("");
    }, [item?.id, item?.my_opinion]);

    async function submitOpinion() {
        setLocalError("");

        if (!decisionType) {
            setLocalError("Bitte NFA, Strafe oder Befangenheit auswählen.");
            return;
        }

        if (!reasoning.trim()) {
            setLocalError("Bitte eine Begründung eingeben.");
            return;
        }

        const payload = {
            decision_type: decisionType,
            conflict_type: null,
            reasoning: reasoning.trim(),
            penalty: null,
        };

        if (decisionType === "conflict") {
            if (!conflictType) {
                setLocalError("Bitte Art der Befangenheit auswählen.");
                return;
            }

            payload.conflict_type = conflictType;
        }

        if (decisionType === "penalty") {
            if (!penalizedDriverId || !penaltyRuleId || !penaltySeconds || !penaltyPoints) {
                setLocalError("Bitte Fahrer, Tatbestand, Strafsekunden und Strafpunkte auswählen.");
                return;
            }

            payload.penalty = {
                penalized_season_driver_id: Number(penalizedDriverId),
                rule_id: Number(penaltyRuleId),
                penalty_seconds: Number(penaltySeconds),
                penalty_points: Number(penaltyPoints),
                penalty_text: penaltyText.trim() || null,
            };
        }

        setBusy(true);

        try {
            await saveRekoOpinion(item.id, payload);
            await onSaved?.();
        } catch (err) {
            setLocalError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div
            style={{
                marginTop: 14,
                padding: 14,
                borderRadius: 16,
                background: existing
                    ? "rgba(139,217,166,.07)"
                    : "rgba(228,254,60,.055)",
                border: existing
                    ? "1px solid rgba(139,217,166,.24)"
                    : "1px solid rgba(228,254,60,.18)",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginBottom: 12,
                }}
            >
                <strong>
                    Deine Bewertung
                </strong>

                {existing ? (
                    <span className="pill pill--am">
                        Erledigt
                    </span>
                ) : (
                    <span className="pill pill--int">
                        Offen
                    </span>
                )}
            </div>

            {isLocked && (
                <div className="alert alert--warn" style={{ marginBottom: 12 }}>
                    Dieser Fall ist nicht mehr offen und kann aktuell nicht bewertet werden.
                </div>
            )}

            <div
                style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    marginBottom: 12,
                }}
            >
                <button
                    type="button"
                    className={`button ${decisionType === "nfa" ? "button--primary" : "button--secondary"}`}
                    onClick={() => setDecisionType("nfa")}
                    disabled={isLocked || busy}
                >
                    NFA
                </button>

                <button
                    type="button"
                    className={`button ${decisionType === "penalty" ? "button--primary" : "button--secondary"}`}
                    onClick={() => setDecisionType("penalty")}
                    disabled={isLocked || busy}
                >
                    Strafe
                </button>

                <button
                    type="button"
                    className={`button ${decisionType === "conflict" ? "button--primary" : "button--secondary"}`}
                    onClick={() => setDecisionType("conflict")}
                    disabled={isLocked || busy}
                >
                    Befangenheit
                </button>
            </div>

            {decisionType === "conflict" && (
                <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", marginBottom: 6 }}>
                        Art der Befangenheit
                    </label>

                    <select
                        className="input rv-select"
                        value={conflictType}
                        onChange={(e) => setConflictType(e.target.value)}
                        disabled={isLocked || busy}
                    >
                        <option value="">Bitte auswählen</option>
                        <option value="self_involved">Ich bin selbst betroffen</option>
                        <option value="teammate_involved">Teamkollege betroffen</option>
                    </select>
                </div>
            )}

            {decisionType === "penalty" && (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: 10,
                        marginBottom: 12,
                    }}
                >
                    <div>
                        <label style={{ display: "block", marginBottom: 6 }}>
                            Strafe für
                        </label>

                        <select
                            className="input rv-select"
                            value={penalizedDriverId}
                            onChange={(e) => setPenalizedDriverId(e.target.value)}
                            disabled={isLocked || busy}
                        >
                            <option value="">Bitte auswählen</option>
                            {drivers.map((driver) => (
                                <option
                                    key={driver.season_driver_id}
                                    value={driver.season_driver_id}
                                >
                                    {driver.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: 6 }}>
                            Tatbestand
                        </label>

                        <select
                            className="input rv-select"
                            value={penaltyRuleId}
                            onChange={(e) => setPenaltyRuleId(e.target.value)}
                            disabled={isLocked || busy}
                        >
                            <option value="">Bitte auswählen</option>
                            {rules.map((rule) => (
                                <option key={rule.id} value={rule.id}>
                                    {rule.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: 6 }}>
                            Strafsekunden
                        </label>

                        <select
                            className="input rv-select"
                            value={penaltySeconds}
                            onChange={(e) => setPenaltySeconds(e.target.value)}
                            disabled={isLocked || busy}
                        >
                            <option value="">Bitte auswählen</option>
                            {[5, 10, 15, 20, 25, 30].map((v) => (
                                <option key={v} value={v}>
                                    {v} Sekunden
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: 6 }}>
                            Strafpunkte
                        </label>

                        <select
                            className="input rv-select"
                            value={penaltyPoints}
                            onChange={(e) => setPenaltyPoints(e.target.value)}
                            disabled={isLocked || busy}
                        >
                            <option value="">Bitte auswählen</option>
                            {[1, 2, 3].map((v) => (
                                <option key={v} value={v}>
                                    {v} Punkt{v > 1 ? "e" : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {decisionType === "penalty" && (
                <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", marginBottom: 6 }}>
                        Zusatztext zur Strafe optional
                    </label>

                    <input
                        className="input"
                        style={{ width: "100%" }}
                        value={penaltyText}
                        onChange={(e) => setPenaltyText(e.target.value)}
                        disabled={isLocked || busy}
                        placeholder="z. B. Vorteil durch Kontakt, unsicheres Zurückkommen ..."
                    />
                </div>
            )}

            <div>
                <label style={{ display: "block", marginBottom: 6 }}>
                    Begründung
                </label>

                <textarea
                    className="input"
                    rows={4}
                    style={{ width: "100%", resize: "vertical" }}
                    value={reasoning}
                    onChange={(e) => setReasoning(e.target.value)}
                    disabled={isLocked || busy}
                    placeholder="Bitte kurze Begründung eintragen ..."
                />
            </div>

            {localError && (
                <div className="alert alert--err" style={{ marginTop: 12 }}>
                    {localError}
                </div>
            )}

            {!isLocked && (
                <div style={{ marginTop: 12 }}>
                    <button
                        type="button"
                        className="btn btn--accent"
                        onClick={submitOpinion}
                        disabled={busy}
                    >
                        {existing ? "Bewertung aktualisieren" : "Bewertung abgeben"}
                    </button>
                </div>
            )}
        </div>
    );
}

function LeadFinalizationPanel({ cases, rules, onReload }) {
    const [selectedCaseId, setSelectedCaseId] = useState("");
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [localError, setLocalError] = useState("");

    const [finalType, setFinalType] = useState("");
    const [finalReasoning, setFinalReasoning] = useState("");
    const [penalizedDriverId, setPenalizedDriverId] = useState("");
    const [ruleId, setRuleId] = useState("");
    const [penaltySeconds, setPenaltySeconds] = useState("");
    const [penaltyPoints, setPenaltyPoints] = useState("");
    const [penaltyText, setPenaltyText] = useState("");

    const selectedCase = detail?.case || null;
    const opinions = Array.isArray(detail?.opinions) ? detail.opinions : [];
    const finalDecision = detail?.final_decision || null;
    const drivers = Array.isArray(selectedCase?.drivers) ? selectedCase.drivers : [];

    async function loadLeadDetail(caseId) {
        if (!caseId) {
            setDetail(null);
            return;
        }

        setLoading(true);
        setLocalError("");

        try {
            const data = await fetchRekoLeadOpinions(caseId);
            setDetail(data);

            const fd = data?.final_decision || null;
            const firstPenalty = fd?.penalties?.[0] || null;

            setFinalType(fd?.final_type || "");
            setFinalReasoning(fd?.final_reasoning || "");
            setPenalizedDriverId(firstPenalty?.penalized_season_driver_id ? String(firstPenalty.penalized_season_driver_id) : "");
            setRuleId(firstPenalty?.rule_id ? String(firstPenalty.rule_id) : "");
            setPenaltySeconds(firstPenalty?.penalty_seconds ? String(firstPenalty.penalty_seconds) : "");
            setPenaltyPoints(firstPenalty?.penalty_points ? String(firstPenalty.penalty_points) : "");
            setPenaltyText(firstPenalty?.penalty_text || "");
        } catch (err) {
            setLocalError(prettyError(err));
            setDetail(null);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!selectedCaseId && cases?.length) {
            const first = cases.find((c) => c.status === "open" || c.status === "finalized") || cases[0];
            if (first?.id) {
                setSelectedCaseId(String(first.id));
            }
        }
    }, [cases, selectedCaseId]);

    useEffect(() => {
        if (!selectedCaseId) return;
        loadLeadDetail(selectedCaseId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCaseId]);

    async function saveFinalDecision() {
        setLocalError("");

        if (!selectedCaseId) {
            setLocalError("Bitte zuerst einen Case auswählen.");
            return;
        }

        if (!finalType) {
            setLocalError("Bitte finale Entscheidung auswählen.");
            return;
        }

        if (!finalReasoning.trim()) {
            setLocalError("Bitte finale Begründung eintragen.");
            return;
        }

        const payload = {
            final_type: finalType,
            final_reasoning: finalReasoning.trim(),
            penalties: [],
        };

        if (finalType === "penalty") {
            if (!penalizedDriverId || !ruleId || !penaltySeconds || !penaltyPoints) {
                setLocalError("Bitte Fahrer, Tatbestand, Strafsekunden und Strafpunkte auswählen.");
                return;
            }

            payload.penalties = [
                {
                    penalized_season_driver_id: Number(penalizedDriverId),
                    rule_id: Number(ruleId),
                    penalty_seconds: Number(penaltySeconds),
                    penalty_points: Number(penaltyPoints),
                    penalty_text: penaltyText.trim() || null,
                },
            ];
        }

        setBusy(true);

        try {
            await saveRekoLeadFinalDecision(selectedCaseId, payload);
            await loadLeadDetail(selectedCaseId);
            await onReload?.();
        } catch (err) {
            setLocalError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function publishFinalDecision() {
        if (!selectedCaseId) return;

        if (!window.confirm("Finale Entscheidung wirklich veröffentlichen? Danach ist sie für Fahrer sichtbar.")) {
            return;
        }

        setBusy(true);
        setLocalError("");

        try {
            await publishRekoLeadFinalDecision(selectedCaseId);
            await loadLeadDetail(selectedCaseId);
            await onReload?.();
        } catch (err) {
            setLocalError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function reopenCase() {
        if (!selectedCaseId) return;

        if (!window.confirm("Case wirklich wieder öffnen?")) {
            return;
        }

        setBusy(true);
        setLocalError("");

        try {
            await reopenRekoLeadCase(selectedCaseId);
            await loadLeadDetail(selectedCaseId);
            await onReload?.();
        } catch (err) {
            setLocalError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    function opinionLabel(opinion) {
        if (opinion.decision_type === "nfa") return "NFA";
        if (opinion.decision_type === "penalty") return "Strafe";
        if (opinion.decision_type === "conflict") {
            if (opinion.conflict_type === "self_involved") return "Befangen: selbst";
            if (opinion.conflict_type === "teammate_involved") return "Befangen: Team";
            return "Befangen";
        }

        return opinion.decision_type || "Unbekannt";
    }

    return (
        <div className="card" style={{ display: "grid", gap: 16 }}>
            <div>
                <h2>Finalisierung</h2>
                <p className="muted">
                    ReKo Lead Bereich: Bewertungen ansehen, finale Entscheidung speichern und veröffentlichen.
                </p>
            </div>

            <div>
                <label style={{ display: "block", marginBottom: 6 }}>
                    Case auswählen
                </label>

                <select
                    className="input rv-select"
                    value={selectedCaseId}
                    onChange={(e) => setSelectedCaseId(e.target.value)}
                >
                    <option value="">Bitte auswählen</option>
                    {cases.map((item) => (
                        <option key={item.id} value={item.id}>
                            {item.case_number} · {item.status}
                        </option>
                    ))}
                </select>
            </div>

            {localError && (
                <div className="alert alert--err">
                    {localError}
                </div>
            )}

            {loading && (
                <div className="muted">
                    Lade Bewertungen …
                </div>
            )}

            {!loading && selectedCase && (
                <>
                    <div
                        style={{
                            border: "1px solid rgba(255,255,255,.10)",
                            borderRadius: 16,
                            padding: 14,
                            background: "rgba(255,255,255,.035)",
                        }}
                    >
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                            <strong>{selectedCase.case_number}</strong>
                            <StatusBadge status={selectedCase.status} />
                            {selectedCase.incident_timestamp && (
                                <span className="muted">Timestamp: {selectedCase.incident_timestamp}</span>
                            )}
                        </div>

                        <div style={{ marginTop: 8 }}>
                            <strong>Fahrer:</strong>{" "}
                            {drivers.length
                                ? drivers.map((d) => d.label).join(" / ")
                                : "keine Fahrer hinterlegt"}
                        </div>

                        <div style={{ marginTop: 4 }}>
                            <strong>Tatbestand:</strong>{" "}
                            {selectedCase.initial_rule?.label || "nicht gesetzt"}
                        </div>

                        {selectedCase.description && (
                            <div className="muted" style={{ marginTop: 8 }}>
                                {selectedCase.description}
                            </div>
                        )}
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                            gap: 12,
                        }}
                    >
                        {opinions.length === 0 && (
                            <div className="muted">
                                Noch keine ReKo-Bewertungen vorhanden.
                            </div>
                        )}

                        {opinions.map((opinion) => (
                            <div
                                key={opinion.id}
                                style={{
                                    border: "1px solid rgba(255,255,255,.10)",
                                    borderRadius: 16,
                                    padding: 14,
                                    background: "rgba(255,255,255,.035)",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        gap: 8,
                                        flexWrap: "wrap",
                                        marginBottom: 8,
                                    }}
                                >
                                    <strong>
                                        {opinion.reko_user?.display_name || `ReKo #${opinion.reko_user_id}`}
                                    </strong>

                                    <span className={
                                        opinion.decision_type === "penalty"
                                            ? "pill pill--pro"
                                            : opinion.decision_type === "nfa"
                                                ? "pill pill--am"
                                                : "pill pill--int"
                                    }>
                                        {opinionLabel(opinion)}
                                    </span>
                                </div>

                                <div className="muted" style={{ whiteSpace: "pre-wrap" }}>
                                    {opinion.reasoning}
                                </div>

                                {opinion.penalties?.length > 0 && (
                                    <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
                                        {opinion.penalties.map((p) => (
                                            <div key={p.id}>
                                                <strong>{p.driver_name || "Fahrer"}</strong>{" "}
                                                · {p.penalty_seconds}s · {p.penalty_points} SP
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div
                        style={{
                            padding: 14,
                            borderRadius: 16,
                            background: "rgba(228,254,60,.055)",
                            border: "1px solid rgba(228,254,60,.18)",
                        }}
                    >
                        <h3 style={{ marginTop: 0 }}>Finale Entscheidung</h3>

                        {finalDecision && (
                            <div className="alert alert--ok" style={{ marginBottom: 12 }}>
                                Finale Entscheidung ist gespeichert.
                                {selectedCase.status === "published" ? " Dieser Case ist veröffentlicht." : " Veröffentlichung steht noch aus."}
                            </div>
                        )}

                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                            <button
                                type="button"
                                className={`button ${finalType === "nfa" ? "button--primary" : "button--secondary"}`}
                                onClick={() => setFinalType("nfa")}
                                disabled={busy || selectedCase.status === "published"}
                            >
                                Final NFA
                            </button>

                            <button
                                type="button"
                                className={`button ${finalType === "penalty" ? "button--primary" : "button--secondary"}`}
                                onClick={() => setFinalType("penalty")}
                                disabled={busy || selectedCase.status === "published"}
                            >
                                Finale Strafe
                            </button>
                        </div>

                        {finalType === "penalty" && (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                                    gap: 10,
                                    marginBottom: 12,
                                }}
                            >
                                <div>
                                    <label style={{ display: "block", marginBottom: 6 }}>Strafe für</label>
                                    <select
                                        className="input rv-select"
                                        value={penalizedDriverId}
                                        onChange={(e) => setPenalizedDriverId(e.target.value)}
                                        disabled={busy || selectedCase.status === "published"}
                                    >
                                        <option value="">Bitte auswählen</option>
                                        {drivers.map((driver) => (
                                            <option key={driver.season_driver_id} value={driver.season_driver_id}>
                                                {driver.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: "block", marginBottom: 6 }}>Tatbestand</label>
                                    <select
                                        className="input rv-select"
                                        value={ruleId}
                                        onChange={(e) => setRuleId(e.target.value)}
                                        disabled={busy || selectedCase.status === "published"}
                                    >
                                        <option value="">Bitte auswählen</option>
                                        {rules.map((rule) => (
                                            <option key={rule.id} value={rule.id}>
                                                {rule.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: "block", marginBottom: 6 }}>Strafsekunden</label>
                                    <select
                                        className="input rv-select"
                                        value={penaltySeconds}
                                        onChange={(e) => setPenaltySeconds(e.target.value)}
                                        disabled={busy || selectedCase.status === "published"}
                                    >
                                        <option value="">Bitte auswählen</option>
                                        {[5, 10, 15, 20, 25, 30].map((v) => (
                                            <option key={v} value={v}>
                                                {v} Sekunden
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: "block", marginBottom: 6 }}>Strafpunkte</label>
                                    <select
                                        className="input rv-select"
                                        value={penaltyPoints}
                                        onChange={(e) => setPenaltyPoints(e.target.value)}
                                        disabled={busy || selectedCase.status === "published"}
                                    >
                                        <option value="">Bitte auswählen</option>
                                        {[1, 2, 3].map((v) => (
                                            <option key={v} value={v}>
                                                {v} Punkt{v > 1 ? "e" : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        {finalType === "penalty" && (
                            <div style={{ marginBottom: 12 }}>
                                <label style={{ display: "block", marginBottom: 6 }}>
                                    Zusatztext optional
                                </label>

                                <input
                                    className="input"
                                    style={{ width: "100%" }}
                                    value={penaltyText}
                                    onChange={(e) => setPenaltyText(e.target.value)}
                                    disabled={busy || selectedCase.status === "published"}
                                    placeholder="z. B. Vorteil durch Kontakt ..."
                                />
                            </div>
                        )}

                        <div>
                            <label style={{ display: "block", marginBottom: 6 }}>
                                Finale Begründung
                            </label>

                            <textarea
                                className="input"
                                rows={4}
                                style={{ width: "100%", resize: "vertical" }}
                                value={finalReasoning}
                                onChange={(e) => setFinalReasoning(e.target.value)}
                                disabled={busy || selectedCase.status === "published"}
                                placeholder="Finale Begründung für Fahreransicht ..."
                            />
                        </div>

                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                            {selectedCase.status !== "published" && (
                                <button
                                    type="button"
                                    className="btn btn--accent"
                                    onClick={saveFinalDecision}
                                    disabled={busy}
                                >
                                    Finale Entscheidung speichern
                                </button>
                            )}

                            {selectedCase.status === "finalized" && (
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={publishFinalDecision}
                                    disabled={busy}
                                >
                                    Veröffentlichen
                                </button>
                            )}

                            {(selectedCase.status === "finalized" || selectedCase.status === "published") && (
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={reopenCase}
                                    disabled={busy}
                                >
                                    Wieder öffnen
                                </button>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default function RekoIncidents() {
    const { user } = useAuth();

    const canAdmin = hasRole(user, "admin");
    const canReko = hasRole(user, "reko") || hasRole(user, "reko_lead");
    const canLead = hasRole(user, "reko_lead");

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const [season, setSeason] = useState(null);
    const [races, setRaces] = useState([]);
    const [raceId, setRaceId] = useState("");

    const [drivers, setDrivers] = useState([]);
    const [rules, setRules] = useState([]);

    const [adminCases, setAdminCases] = useState([]);
    const [rekoCases, setRekoCases] = useState([]);

    const [editingCaseId, setEditingCaseId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);

    const selectedRace = useMemo(() => {
        return races.find((r) => String(r.id) === String(raceId)) || null;
    }, [races, raceId]);

    const draftCount = useMemo(() => {
        return adminCases.filter((c) => c.status === "draft").length;
    }, [adminCases]);

    async function loadBaseData() {
        setLoading(true);
        setError("");

        try {
            const raceData = await fetchRekoRaces({ seriesKey: "gt3x" });
            const driverData = await fetchRekoDrivers({ seriesKey: "gt3x" });
            const ruleData = await fetchRekoRules();

            setSeason(raceData.season || null);
            setRaces(Array.isArray(raceData.races) ? raceData.races : []);
            setDrivers(Array.isArray(driverData.drivers) ? driverData.drivers : []);
            setRules(Array.isArray(ruleData.rules) ? ruleData.rules : []);

            const initialRaceId =
                raceData.current_race_id ||
                raceData.races?.[0]?.id ||
                "";

            setRaceId(String(initialRaceId || ""));
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setLoading(false);
        }
    }

    async function loadCases(nextRaceId = raceId) {
        if (!nextRaceId) {
            setAdminCases([]);
            setRekoCases([]);
            return;
        }

        setError("");

        try {
            if (canAdmin) {
                const data = await fetchRekoAdminCases({ raceId: nextRaceId });
                setAdminCases(Array.isArray(data.cases) ? data.cases : []);
            }

            if (canReko) {
                const data = await fetchRekoCases({ raceId: nextRaceId });
                setRekoCases(Array.isArray(data.cases) ? data.cases : []);
            }
        } catch (err) {
            setError(prettyError(err));
        }
    }

    useEffect(() => {
        loadBaseData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!raceId) return;
        loadCases(raceId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [raceId, canAdmin, canReko]);

    function updateForm(key, value) {
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    }

    function resetForm() {
        setEditingCaseId(null);
        setForm(EMPTY_FORM);
    }

    function startEdit(item) {
        const involved = Array.isArray(item.drivers) ? item.drivers : [];

        setEditingCaseId(item.id);
        setForm({
            incident_timestamp: item.incident_timestamp || "",
            initial_rule_id: item.initial_rule_id ? String(item.initial_rule_id) : "",
            driver_1_season_driver_id: involved[0]?.season_driver_id ? String(involved[0].season_driver_id) : "",
            driver_2_season_driver_id: involved[1]?.season_driver_id ? String(involved[1].season_driver_id) : "",
            description: item.description || "",
        });
    }

    async function saveDraft() {
        if (!raceId) {
            setError("Bitte zuerst ein Rennen auswählen.");
            return;
        }

        if (!form.driver_1_season_driver_id && !form.driver_2_season_driver_id) {
            setError("Bitte mindestens einen Fahrer auswählen.");
            return;
        }

        setBusy(true);
        setError("");

        const payload = {
            race_id: Number(raceId),
            incident_timestamp: form.incident_timestamp.trim() || null,
            initial_rule_id: form.initial_rule_id ? Number(form.initial_rule_id) : null,
            driver_1_season_driver_id: form.driver_1_season_driver_id ? Number(form.driver_1_season_driver_id) : null,
            driver_2_season_driver_id: form.driver_2_season_driver_id ? Number(form.driver_2_season_driver_id) : null,
            description: form.description.trim() || null,
        };

        try {
            if (editingCaseId) {
                await updateRekoAdminCase(editingCaseId, payload);
            } else {
                await createRekoAdminCase(payload);
            }

            resetForm();
            await loadCases(raceId);
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function deleteDraft(caseId) {
        if (!window.confirm("Draft wirklich löschen?")) return;

        setBusy(true);
        setError("");

        try {
            await deleteRekoAdminCase(caseId);
            await loadCases(raceId);
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function openBulk() {
        if (!raceId) return;

        if (!window.confirm(`${draftCount} Draft(s) wirklich an die ReKo freigeben?`)) {
            return;
        }

        setBusy(true);
        setError("");

        try {
            await openBulkRekoAdminCases({ raceId: Number(raceId) });
            await loadCases(raceId);
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    if (loading) {
        return (
            <div className="card">
                <h1>ReKo Incidents</h1>
                <p className="muted">Lade Daten …</p>
            </div>
        );
    }

    return (
        <div style={{ display: "grid", gap: 16 }}>
            <div className="card">
                <h1>ReKo Incidents</h1>

                <p className="muted">
                    Zentrale Erfassung und spätere Bewertung der RaceVision Incidents.
                </p>

                <div style={{ marginTop: 12, display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <div>
                        <label style={{ display: "block", marginBottom: 6 }}>
                            Rennen
                        </label>

                        <select
                            className="input rv-select"
                            value={raceId}
                            onChange={(e) => {
                                setRaceId(e.target.value);
                                resetForm();
                            }}
                        >
                            {races.map((race) => (
                                <option key={race.id} value={race.id}>
                                    {race.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {season && (
                        <div style={{ alignSelf: "end", opacity: 0.85 }}>
                            Saison: <strong>{season.display_name}</strong>
                        </div>
                    )}

                    {selectedRace && (
                        <div style={{ alignSelf: "end", opacity: 0.85 }}>
                            Aktuell gewählt: <strong>{selectedRace.label}</strong>
                        </div>
                    )}
                </div>

                {error && (
                    <div className="alert alert--err" style={{ marginTop: 14 }}>
                        {error}
                    </div>
                )}
            </div>

            {canAdmin && (
                <div className="card">
                    <h2>{editingCaseId ? "Incident bearbeiten" : "Neuen Incident erfassen"}</h2>

                    <p className="muted">
                        Die Case-Nummer wird automatisch erzeugt, z. B. R3-01.
                        Incidents bleiben zuerst als Draft und werden gesammelt an die ReKo freigegeben.
                    </p>

                    <div
                        style={{
                            marginTop: 16,
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: 12,
                        }}
                    >
                        <div>
                            <label style={{ display: "block", marginBottom: 6 }}>
                                Timestamp ingame
                            </label>
                            <input
                                className="input"
                                value={form.incident_timestamp}
                                onChange={(e) => updateForm("incident_timestamp", e.target.value)}
                                placeholder="z. B. 00:14:32"
                            />
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: 6 }}>
                                Tatbestand Regelwerk
                            </label>
                            <select
                                className="input rv-select"
                                value={form.initial_rule_id}
                                onChange={(e) => updateForm("initial_rule_id", e.target.value)}
                            >
                                <option value="">Bitte auswählen</option>
                                {rules.map((rule) => (
                                    <option key={rule.id} value={rule.id}>
                                        {rule.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: 6 }}>
                                Fahrer 1
                            </label>
                            <select
                                className="input rv-select"
                                value={form.driver_1_season_driver_id}
                                onChange={(e) => updateForm("driver_1_season_driver_id", e.target.value)}
                            >
                                <option value="">Bitte auswählen</option>
                                {drivers.map((driver) => (
                                    <option key={driver.season_driver_id} value={driver.season_driver_id}>
                                        {driver.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: 6 }}>
                                Fahrer 2
                            </label>
                            <select
                                className="input rv-select"
                                value={form.driver_2_season_driver_id}
                                onChange={(e) => updateForm("driver_2_season_driver_id", e.target.value)}
                            >
                                <option value="">Bitte auswählen</option>
                                {drivers.map((driver) => (
                                    <option key={driver.season_driver_id} value={driver.season_driver_id}>
                                        {driver.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div style={{ marginTop: 12 }}>
                        <label style={{ display: "block", marginBottom: 6 }}>
                            Beschreibung / Orga-Notiz
                        </label>
                        <textarea
                            className="input"
                            rows={4}
                            style={{ width: "100%", resize: "vertical" }}
                            value={form.description}
                            onChange={(e) => updateForm("description", e.target.value)}
                            placeholder="Kurze Beschreibung des Vorfalls …"
                        />
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
                        <button className="btn btn--accent" type="button" onClick={saveDraft} disabled={busy}>
                            {editingCaseId ? "Änderungen speichern" : "Als Draft speichern"}
                        </button>

                        {editingCaseId && (
                            <button className="btn" type="button" onClick={resetForm} disabled={busy}>
                                Bearbeitung abbrechen
                            </button>
                        )}
                    </div>
                </div>
            )}

            {canAdmin && (
                <div className="card">
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            flexWrap: "wrap",
                            alignItems: "center",
                        }}
                    >
                        <div>
                            <h2>Gesammelte Incidents</h2>
                            <p className="muted">
                                Drafts können bearbeitet oder gelöscht werden. Freigegebene Fälle bleiben editierbar,
                                bis sie finalisiert sind.
                            </p>
                        </div>

                        <button
                            className="btn btn--accent"
                            type="button"
                            onClick={openBulk}
                            disabled={busy || draftCount <= 0}
                        >
                            Alle Drafts an ReKo freigeben ({draftCount})
                        </button>
                    </div>

                    <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
                        {!adminCases.length && (
                            <div className="muted">
                                Noch keine Incidents für dieses Rennen erfasst.
                            </div>
                        )}

                        {adminCases.map((item) => (
                            <div
                                key={item.id}
                                style={{
                                    border: "1px solid rgba(255,255,255,.10)",
                                    borderRadius: 14,
                                    padding: 14,
                                    background: "rgba(255,255,255,.035)",
                                    display: "grid",
                                    gap: 8,
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        gap: 10,
                                        flexWrap: "wrap",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                                        <strong>{item.case_number}</strong>
                                        <StatusBadge status={item.status} />
                                        {item.incident_timestamp && (
                                            <span className="muted">
                                                Timestamp: {item.incident_timestamp}
                                            </span>
                                        )}
                                    </div>

                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                        {["draft", "open"].includes(item.status) && (
                                            <button className="btn" type="button" onClick={() => startEdit(item)} disabled={busy}>
                                                Bearbeiten
                                            </button>
                                        )}

                                        {item.status === "draft" && (
                                            <button className="btn" type="button" onClick={() => deleteDraft(item.id)} disabled={busy}>
                                                Löschen
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <strong>Fahrer:</strong>{" "}
                                    {item.drivers?.length
                                        ? item.drivers.map((d) => d.label).join(" / ")
                                        : "keine Fahrer hinterlegt"}
                                </div>

                                <div>
                                    <strong>Tatbestand:</strong>{" "}
                                    {item.initial_rule?.label || "nicht gesetzt"}
                                </div>

                                {item.description && (
                                    <div className="muted">
                                        {item.description}
                                    </div>
                                )}

                            </div>
                        ))}
                    </div>
                </div>
            )}

            {canReko && (
                <div className="card">
                    <h2>Meine ReKo-Fälle</h2>

                    <p className="muted">
                        Phase 1: Hier siehst du freigegebene Fälle. Die eigentliche Bewertung mit NFA, Strafe oder Befangenheit kommt im nächsten Schritt.
                    </p>

                    <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
                        {!rekoCases.length && (
                            <div className="muted">
                                Keine freigegebenen ReKo-Fälle für dieses Rennen.
                            </div>
                        )}

                        {rekoCases.map((item) => (
                            <div
                                key={item.id}
                                style={{
                                    border: "1px solid rgba(255,255,255,.10)",
                                    borderRadius: 14,
                                    padding: 14,
                                    background: "rgba(255,255,255,.035)",
                                    display: "grid",
                                    gap: 8,
                                }}
                            >
                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                                    <strong>{item.case_number}</strong>
                                    <StatusBadge status={item.status} />
                                    {item.incident_timestamp && (
                                        <span className="muted">
                                            Timestamp: {item.incident_timestamp}
                                        </span>
                                    )}
                                </div>

                                <div>
                                    <strong>Fahrer:</strong>{" "}
                                    {item.drivers?.length
                                        ? item.drivers.map((d) => d.label).join(" / ")
                                        : "keine Fahrer hinterlegt"}
                                </div>

                                <div>
                                    <strong>Tatbestand:</strong>{" "}
                                    {item.initial_rule?.label || "nicht gesetzt"}
                                </div>

                                {item.description && (
                                    <div className="muted">
                                        {item.description}
                                    </div>
                                )}

                                <OpinionEditor
                                    item={item}
                                    rules={rules}
                                    onSaved={() => loadCases(raceId)}
                                />

                            </div>
                        ))}
                    </div>

                    {canLead && (
                        <div className="alert alert--ok" style={{ marginTop: 16 }}>
                            ReKo Lead erkannt. Finalisierung und Übersicht aller Bewertungen bauen wir in Phase 2/3.
                        </div>
                    )}
                </div>
            )}

            {canLead && (
                <LeadFinalizationPanel
                    cases={rekoCases.length ? rekoCases : adminCases.filter((c) =>
                        ["open", "finalized", "published"].includes(c.status)
                    )}
                    rules={rules}
                    onReload={() => loadCases(raceId)}
                />
            )}

        </div>
    );
}